import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Check, Sticker, Video, Clock, ShieldCheck, Ruler, Layers, Zap, Mail, Info } from "lucide-react";

const stickerPacks = [
  { key: "sticker-10",  qty: 10,  price: 20, each: 2.00, blurb: "Perfect sample pack or small batch for personal use." },
  { key: "sticker-50",  qty: 50,  price: 55, each: 1.10, best: true, blurb: "Our most popular pack — ideal for events, launches and gifting." },
  { key: "sticker-100", qty: 100, price: 85, each: 0.85, blurb: "Bulk value for shops, teams and giveaways." },
];

const factors = [
  { icon: Layers,  title: "Complexity",      note: "Layering, photo etching, inlays and fine artwork take more time." },
  { icon: Ruler,   title: "Size",            note: "Larger surfaces mean more passes and more material." },
  { icon: Sticker, title: "Material",        note: "Walnut, brass, steel, slate, leather — each has its own cost." },
  { icon: Zap,     title: "Turnaround",      note: "Standard, expedited, or 48-hour rush — you pick the pace." },
];

const materials = [
  { name: "Walnut", tone: "Rich espresso grain, warm undertone." },
  { name: "Maple", tone: "Pale cream, tight grain, high contrast." },
  { name: "Cherry", tone: "Reddish patina that deepens with age." },
  { name: "Brass", tone: "Golden warmth, satin brushed finish." },
  { name: "Stainless Steel", tone: "Cool, industrial precision." },
  { name: "Copper", tone: "Rose gold living finish." },
  { name: "Slate", tone: "Charcoal matte, photographic depth." },
  { name: "Leather", tone: "Full-grain, deep debossed character." },
];

export default function Products() {
  return (
    <div data-testid="products-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="uppercase text-xs tracking-[0.3em] font-bold" style={{color: "var(--secondary)"}}>Products & Services</div>
      <h1 className="font-serif text-5xl sm:text-6xl mt-3 max-w-3xl leading-none" style={{color: "var(--primary)"}}>Priced for your project, not off a shelf.</h1>
      <p className="mt-5 max-w-2xl text-[color:var(--text-muted)] leading-relaxed">
        Every custom engraving is quoted individually. Share your idea in the Custom Order form and
        we'll review it and send you a firm price within <span className="font-semibold" style={{color:"var(--primary)"}}>24 hours</span>.
        No surprises — if your design needs any clarification, you'll have the option to hop on a <span className="font-semibold" style={{color:"var(--primary)"}}>free video call directly with the engraver</span> to walk through the details together.
      </p>

      {/* HOW PRICING WORKS */}
      <div data-testid="pricing-model" className="mt-12 grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3 bg-white rounded-lg border hairline p-8">
          <div className="uppercase text-xs tracking-[0.3em] font-bold" style={{color: "var(--secondary)"}}>How pricing works</div>
          <h2 className="font-serif text-3xl sm:text-4xl mt-3" style={{color: "var(--primary)"}}>Four factors. One quote.</h2>
          <div className="mt-8 grid sm:grid-cols-2 gap-6">
            {factors.map((f) => (
              <div key={f.title} data-testid={`factor-${f.title.toLowerCase()}`} className="flex gap-4">
                <div className="shrink-0 w-10 h-10 rounded-full border hairline flex items-center justify-center" style={{color: "var(--accent-metal)"}}>
                  <f.icon size={16} strokeWidth={1.75}/>
                </div>
                <div>
                  <div className="font-serif text-lg" style={{color: "var(--primary)"}}>{f.title}</div>
                  <div className="text-xs text-[color:var(--text-muted)] mt-1 leading-relaxed">{f.note}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="metal-line my-8"/>
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="flex items-start gap-3">
              <Clock size={16} strokeWidth={1.75} style={{color: "var(--accent-metal)"}} className="mt-0.5 shrink-0"/>
              <div>
                <div className="text-sm font-semibold" style={{color: "var(--primary)"}}>Quote in 24 hours</div>
                <div className="text-xs text-[color:var(--text-muted)] mt-1">We review every custom order within one business day.</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Video size={16} strokeWidth={1.75} style={{color: "var(--accent-metal)"}} className="mt-0.5 shrink-0"/>
              <div>
                <div className="text-sm font-semibold" style={{color: "var(--primary)"}}>Free video chat</div>
                <div className="text-xs text-[color:var(--text-muted)] mt-1">Hop on a call with the engraver to dial in your design — no charge.</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <ShieldCheck size={16} strokeWidth={1.75} style={{color: "var(--accent-metal)"}} className="mt-0.5 shrink-0"/>
              <div>
                <div className="text-sm font-semibold" style={{color: "var(--primary)"}}>Card on file</div>
                <div className="text-xs text-[color:var(--text-muted)] mt-1">Securely saved via Stripe — only charged after you approve the quote.</div>
              </div>
            </div>
          </div>
        </div>

        {/* MILITARY DISCOUNT CARD */}
        <aside data-testid="military-discount" className="lg:col-span-2 rounded-lg border hairline p-8 flex flex-col" style={{background: "linear-gradient(160deg, rgba(197,160,89,0.14) 0%, rgba(197,160,89,0.03) 100%)"}}>
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} strokeWidth={1.75} style={{color: "var(--accent-metal)"}}/>
            <div className="uppercase text-xs tracking-[0.3em] font-bold" style={{color: "var(--accent-metal)"}}>Thank you for serving</div>
          </div>
          <div className="font-serif text-4xl mt-3 leading-tight" style={{color: "var(--primary)"}}>10% off, every order.</div>
          <p className="mt-4 text-sm text-[color:var(--text-muted)] leading-relaxed">
            Active-duty and veteran military, first responders (fire, EMS, police), and their <span className="font-semibold">immediate family members</span> receive 10% off every order — including sticker packs and custom projects.
          </p>
          <ul className="mt-5 space-y-2 text-sm">
            <li className="flex items-start gap-2"><Check size={14} strokeWidth={1.5} className="mt-1 shrink-0" style={{color: "var(--accent-metal)"}}/>Check the discount box on the order form</li>
            <li className="flex items-start gap-2"><Check size={14} strokeWidth={1.5} className="mt-1 shrink-0" style={{color: "var(--accent-metal)"}}/>We'll request simple proof (ID or verification) to confirm</li>
            <li className="flex items-start gap-2"><Check size={14} strokeWidth={1.5} className="mt-1 shrink-0" style={{color: "var(--accent-metal)"}}/>Discount guaranteed once verified</li>
          </ul>
          <Link to="/custom-order" className="mt-auto pt-6">
            <Button data-testid="military-cta" className="w-full rounded-full py-6 btn-primary">Start your discounted order</Button>
          </Link>
        </aside>
      </div>

      {/* MAIN CTA */}
      <div className="mt-16 flex flex-wrap items-center justify-between gap-6 rounded-lg border hairline p-8 bg-white">
        <div>
          <div className="font-serif text-3xl" style={{color: "var(--primary)"}}>Have a project in mind?</div>
          <p className="mt-2 text-sm text-[color:var(--text-muted)] max-w-xl">Tell us the material, size and vision. We'll review it and reply within 24 hours with a firm quote and a timeline.</p>
        </div>
        <div className="flex gap-3">
          <Link to="/design-studio"><Button data-testid="design-cta" variant="outline" className="rounded-full px-6 py-6">Preview in Design Studio</Button></Link>
          <Link to="/custom-order"><Button data-testid="request-quote-cta" className="rounded-full px-6 py-6 btn-primary">Request a quote</Button></Link>
        </div>
      </div>

      {/* STICKER PACKS */}
      <div className="mt-24">
        <div className="flex items-center gap-2">
          <Sticker size={14} strokeWidth={2} style={{color: "var(--accent-metal)"}}/>
          <div className="uppercase text-xs tracking-[0.3em] font-bold" style={{color: "var(--secondary)"}}>Sticker Packs · Fixed Pricing</div>
        </div>
        <h2 className="font-serif text-4xl sm:text-5xl mt-3" style={{color: "var(--primary)"}}>Full-color die-cut vinyl, by the pack.</h2>
        <p className="mt-4 max-w-2xl text-[color:var(--text-muted)] leading-relaxed">Weather-resistant, dishwasher-safe vinyl in any shape. Upload your artwork or let us design it. Military & first responder discount applies here too.</p>
        <div data-testid="single-design-note" className="mt-4 inline-flex items-start gap-2 rounded-full border hairline py-2 px-4 text-xs" style={{background: "rgba(197,160,89,0.08)", color: "var(--primary)"}}>
          <Info size={14} strokeWidth={1.75} style={{color: "var(--accent-metal)"}} className="mt-0.5 shrink-0"/>
          <span>Pack prices are for <span className="font-semibold">one design</span>. Need a variety of designs on one invoice? <Link to="/sticker-mix" className="underline underline-offset-2 font-semibold">Build a mix</Link> and we'll quote it.</span>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {stickerPacks.map((p) => (
            <div key={p.key} data-testid={`sticker-pack-${p.qty}`} className={`relative bg-white p-8 rounded-lg border hairline card-lift flex flex-col ${p.best ? "ring-2 ring-[color:var(--accent-metal)]" : ""}`}>
              {p.best && (
                <div className="absolute -top-3 left-6 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] bg-[color:var(--accent-metal)] text-white">Most popular</div>
              )}
              <div className="font-serif text-5xl" style={{color: "var(--primary)"}}>{p.qty}</div>
              <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--text-muted)] mt-1">Stickers</div>
              <div className="mt-5 flex items-baseline gap-2">
                <span className="font-serif text-4xl" style={{color: "var(--primary)"}}>${p.price}</span>
                <span className="text-xs text-[color:var(--text-muted)]">${p.each.toFixed(2)} each</span>
              </div>
              <p className="mt-4 text-sm text-[color:var(--text-muted)] leading-relaxed">{p.blurb}</p>
              <div className="metal-line my-6"/>
              <ul className="space-y-3 flex-1">
                <li className="flex items-start gap-2 text-sm"><Check size={16} strokeWidth={1.5} style={{color: "var(--accent-metal)"}} className="mt-0.5 flex-shrink-0"/><span>Any shape, kiss-cut vinyl</span></li>
                <li className="flex items-start gap-2 text-sm"><Check size={16} strokeWidth={1.5} style={{color: "var(--accent-metal)"}} className="mt-0.5 flex-shrink-0"/><span>Up to 4 inches on longest side</span></li>
                <li className="flex items-start gap-2 text-sm"><Check size={16} strokeWidth={1.5} style={{color: "var(--accent-metal)"}} className="mt-0.5 flex-shrink-0"/><span>Weatherproof + dishwasher-safe</span></li>
                <li className="flex items-start gap-2 text-sm"><Check size={16} strokeWidth={1.5} style={{color: "var(--accent-metal)"}} className="mt-0.5 flex-shrink-0"/><span>Free digital proof before print</span></li>
              </ul>
              <Link to={`/design-studio?mode=sticker&tier=${p.key}`} className="mt-8">
                <Button data-testid={`sticker-cta-${p.qty}`} className={`w-full rounded-full py-6 ${p.best ? "btn-primary" : ""}`} variant={p.best ? "default" : "outline"}>Design {p.qty} Pack · ${p.price}</Button>
              </Link>
            </div>
          ))}
        </div>

        {/* MORE THAN 100 STICKERS CTA — split into two columns: bulk + mix */}
        <div className="mt-8 grid sm:grid-cols-2 gap-5">
          <div data-testid="sticker-mix-cta" className="rounded-lg border hairline p-6 flex flex-col justify-between" style={{background: "linear-gradient(135deg, rgba(197,160,89,0.08), rgba(74,43,22,0.04))"}}>
            <div>
              <div className="font-serif text-2xl" style={{color: "var(--primary)"}}>Want a variety of designs?</div>
              <p className="mt-1 text-sm text-[color:var(--text-muted)]">Build a mix with multiple designs and set quantities for each — we'll quote the whole order together.</p>
            </div>
            <Link to="/sticker-mix" className="mt-4 self-start"><Button data-testid="sticker-mix-btn" className="rounded-full px-6 py-6 btn-primary">Build a mix</Button></Link>
          </div>
          <div data-testid="sticker-bulk-cta" className="rounded-lg border hairline p-6 flex flex-col justify-between" style={{background: "linear-gradient(135deg, rgba(197,160,89,0.08), rgba(74,43,22,0.04))"}}>
            <div>
              <div className="font-serif text-2xl" style={{color: "var(--primary)"}}>Need more than 100 stickers?</div>
              <p className="mt-1 text-sm text-[color:var(--text-muted)]">Volume pricing beyond 100 — events, retail, giveaways and corporate orders. Same-day quotes.</p>
            </div>
            <Link to="/bulk" className="mt-4 self-start"><Button data-testid="sticker-bulk-btn" variant="outline" className="rounded-full px-6 py-6">Go to bulk orders</Button></Link>
          </div>
        </div>
      </div>
      <div className="mt-24">
        <div className="uppercase text-xs tracking-[0.3em] font-bold" style={{color: "var(--secondary)"}}>Materials</div>
        <h2 className="font-serif text-4xl sm:text-5xl mt-3" style={{color: "var(--primary)"}}>Only non-flammable surfaces.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
          {materials.map((m) => (
            <div key={m.name} data-testid={`material-${m.name.toLowerCase().replace(/\s+/g,'-')}`} className="p-6 rounded-lg border hairline bg-white card-lift">
              <div className="font-serif text-2xl" style={{color: "var(--primary)"}}>{m.name}</div>
              <div className="mt-2 text-sm text-[color:var(--text-muted)] leading-relaxed">{m.tone}</div>
            </div>
          ))}
        </div>

        {/* MATERIAL NOT LISTED → CONTACT */}
        <Link to="/contact" data-testid="material-not-listed" className="group mt-8 block rounded-lg border hairline p-6 bg-white card-lift">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center" style={{background: "rgba(197,160,89,0.14)", color: "var(--accent-metal)"}}>
                <Mail size={18} strokeWidth={1.75}/>
              </div>
              <div>
                <div className="font-serif text-2xl" style={{color: "var(--primary)"}}>Don't see your material?</div>
                <p className="mt-1 text-sm text-[color:var(--text-muted)] max-w-xl">If what you want to engrave isn't listed above, send us a quick email — chances are we can source it or already work with something similar.</p>
              </div>
            </div>
            <Button data-testid="material-contact-btn" variant="outline" className="rounded-full px-6 py-6 group-hover:bg-[color:var(--primary)] group-hover:text-white transition-colors">Email the studio</Button>
          </div>
        </Link>
      </div>
    </div>
  );
}
